import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  List,
  message,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import {
  CheckOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  RobotOutlined,
} from '@ant-design/icons';

import {
  normalizeVerifiedReasonsJson,
  type JsonTitleDescriptionItem,
  type PartnerShopReviewSummary,
  type VerifiedReasonsJson,
} from '../PartnerCreatePage';

import {
  generateReviewSummaryDraft,
  listReviewSummaryDrafts,
  type ReviewSummaryDraftItem,
} from '@/api/partnerShops';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;



type ReviewSummaryDraftView = ReviewSummaryDraftItem & {
  draftTitle?: string | null;
  title?: string | null;
  generatedAt?: string | null;
  createdAt?: string | null;
  usedReviewCount?: number | null;
  editorNote?: string | null;

  verifiedReasonsJson?: VerifiedReasonsJson | JsonTitleDescriptionItem[];
  prosJson?: JsonTitleDescriptionItem[];
  consJson?: JsonTitleDescriptionItem[];
  checkTipsJson?: JsonTitleDescriptionItem[];
  keywordsJson?: string[];
  sourceCafeArticleIdsJson?: number[];
  plannerText?: string | null;
};

interface ReviewSummaryStepProps {
  shopName: string;
  sourceCafeExtractedShopIds: number[];

  partnerShopReviewSummary: PartnerShopReviewSummary;
  onChangePartnerShopReviewSummary: (
    nextPartnerShopReviewSummary: PartnerShopReviewSummary
  ) => void;

  verifiedReasonsJson: VerifiedReasonsJson;
  onChangeVerifiedReasonsJson: (
    nextVerifiedReasonsJson: VerifiedReasonsJson
  ) => void;

  partnerShopId?: number;
}

function toNumber(value: unknown, fallback: number) {
  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) {
    return fallback;
  }

  return numberValue;
}

function updateTextItem(
  items: JsonTitleDescriptionItem[],
  index: number,
  patch: Partial<JsonTitleDescriptionItem>
) {
  return items.map((item, itemIndex) => {
    if (itemIndex !== index) {
      return item;
    }

    return {
      ...item,
      ...patch,
    };
  });
}

function removeTextItem(items: JsonTitleDescriptionItem[], index: number) {
  return items.filter((_, itemIndex) => itemIndex !== index);
}

function getDraftTitle(draft: ReviewSummaryDraftView) {
  return draft.draftTitle || draft.title || `초안 #${draft.id}`;
}

function getDraftDateText(draft: ReviewSummaryDraftView) {
  const dateValue = draft.generatedAt || draft.createdAt;

  if (!dateValue) {
    return '';
  }

  return String(dateValue).slice(0, 10);
}

function getDraftSourceCount(draft: ReviewSummaryDraftView) {
  if (draft.usedReviewCount !== undefined && draft.usedReviewCount !== null) {
    return Number(draft.usedReviewCount);
  }

  if (Array.isArray(draft.sourceCafeArticleIdsJson)) {
    return draft.sourceCafeArticleIdsJson.length;
  }

  return Number(draft.evaluatedReviewCount || draft.totalMentionCount || 0);
}

function toPartnerShopReviewSummary(
  draft: ReviewSummaryDraftView,
  previousPartnerShopReviewSummary: PartnerShopReviewSummary
): PartnerShopReviewSummary {
  return {
    ...previousPartnerShopReviewSummary,

    importedLlmDraftId: Number(draft.id),

    totalMentionCount: toNumber(draft.totalMentionCount, 0),
    evaluatedReviewCount: toNumber(draft.evaluatedReviewCount, 0),
    directReviewCount: toNumber(draft.directReviewCount, 0),
    indirectReviewCount: toNumber(draft.indirectReviewCount, 0),
    mentionedOnlyCount: toNumber(draft.mentionedOnlyCount, 0),

    summaryText: draft.summaryText || '',
    plannerText: draft.plannerText || '',
    prosJson: Array.isArray(draft.prosJson) ? draft.prosJson : [],
    consJson: Array.isArray(draft.consJson) ? draft.consJson : [],
    keywordsJson: Array.isArray(draft.keywordsJson) ? draft.keywordsJson : [],
    sourceCafeArticleIdsJson: Array.isArray(draft.sourceCafeArticleIdsJson)
      ? draft.sourceCafeArticleIdsJson
      : [],

    status: 'READY',
    delYn: 0,
  };
}

function TextItemEditor({
  title,
  items,
  onChange,
  addButtonText,
  titlePlaceholder,
  descriptionPlaceholder,
}: {
  title: string;
  items: JsonTitleDescriptionItem[];
  onChange: (nextItems: JsonTitleDescriptionItem[]) => void;
  addButtonText: string;
  titlePlaceholder: string;
  descriptionPlaceholder: string;
}) {
  return (
    <Card size="small" title={title}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {items.length === 0 && (
          <Text type="secondary">아직 입력된 내용이 없습니다.</Text>
        )}

        {items.map((item, index) => (
          <Card
            key={`${title}-${index}`}
            size="small"
            bodyStyle={{ padding: 12 }}
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <Text strong>{index + 1}</Text>

                <Button
                  danger
                  size="small"
                  type="text"
                  icon={<MinusCircleOutlined />}
                  onClick={() => onChange(removeTextItem(items, index))}
                >
                  삭제
                </Button>
              </div>

              <Input
                value={item.title}
                placeholder={titlePlaceholder}
                onChange={(event) =>
                  onChange(
                    updateTextItem(items, index, {
                      title: event.target.value,
                    })
                  )
                }
              />

              <TextArea
                value={item.description}
                rows={3}
                placeholder={descriptionPlaceholder}
                onChange={(event) =>
                  onChange(
                    updateTextItem(items, index, {
                      description: event.target.value,
                    })
                  )
                }
              />
            </Space>
          </Card>
        ))}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() =>
            onChange([
              ...items,
              {
                title: '',
                description: '',
              },
            ])
          }
        >
          {addButtonText}
        </Button>
      </Space>
    </Card>
  );
}

export default function ReviewSummaryStep({
  shopName,
  sourceCafeExtractedShopIds,
  partnerShopReviewSummary,
  onChangePartnerShopReviewSummary,
  verifiedReasonsJson,
  onChangeVerifiedReasonsJson,
  partnerShopId,
}: ReviewSummaryStepProps) {
  const [loadingDrafts, setLoadingDrafts] = useState<boolean>(false);
  const [generatingDraft, setGeneratingDraft] = useState<boolean>(false);
  const [reviewSummaryDrafts, setReviewSummaryDrafts] = useState<
    ReviewSummaryDraftView[]
  >([]);
  const [selectedDraftId, setSelectedDraftId] = useState<number | null>(
    partnerShopReviewSummary.importedLlmDraftId
  );

  const hasSourceCafeExtractedShops = sourceCafeExtractedShopIds.length > 0;

  const loadReviewSummaryDrafts = async () => {
    if (!hasSourceCafeExtractedShops) {
      setReviewSummaryDrafts([]);
      return;
    }

    setLoadingDrafts(true);

    try {
      const result = await listReviewSummaryDrafts({
        cafeExtractedShopIds: sourceCafeExtractedShopIds,
        partnerShopId,
      });

      setReviewSummaryDrafts((result.list || []) as ReviewSummaryDraftView[]);
    } catch (error) {
      console.error(
        '[ReviewSummaryStep] failed to load review summary drafts',
        error
      );

      const errorMessage =
        error instanceof Error
          ? error.message
          : '후기 요약 초안 목록을 불러오지 못했습니다.';

      message.error(errorMessage);
    } finally {
      setLoadingDrafts(false);
    }
  };

  useEffect(() => {
    loadReviewSummaryDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceCafeExtractedShopIds.join(','), partnerShopId]);

  const handleGenerateReviewSummaryDraft = async () => {
    if (!hasSourceCafeExtractedShops) {
      message.warning('먼저 연결할 업체를 선택해 주세요.');
      return;
    }

    setGeneratingDraft(true);

    try {
      const result = await generateReviewSummaryDraft({
        shopName,
        cafeExtractedShopIds: sourceCafeExtractedShopIds,
        partnerShopId,
      });

      const nextDraft = result.draft as ReviewSummaryDraftView;

      const nextReviewSummaryDrafts = [
        nextDraft,
        ...reviewSummaryDrafts.filter(
          (draft) => Number(draft.id) !== Number(nextDraft.id)
        ),
      ];

      setReviewSummaryDrafts(nextReviewSummaryDrafts);
      setSelectedDraftId(Number(nextDraft.id));

      onChangePartnerShopReviewSummary(
        toPartnerShopReviewSummary(nextDraft, partnerShopReviewSummary)
      );

      onChangeVerifiedReasonsJson(
        normalizeVerifiedReasonsJson(
          nextDraft.verifiedReasonsJson,
          verifiedReasonsJson
        )
      );

      message.success('새 후기 요약 초안을 생성했습니다.');
    } catch (error) {
      console.error(
        '[ReviewSummaryStep] failed to generate review summary draft',
        error
      );

      const errorMessage =
        error instanceof Error
          ? error.message
          : '후기 요약 초안 생성 중 오류가 발생했습니다.';

      message.error(errorMessage);
    } finally {
      setGeneratingDraft(false);
    }
  };

  const handleUseReviewSummaryDraft = (draft: ReviewSummaryDraftView) => {
    setSelectedDraftId(Number(draft.id));

    onChangePartnerShopReviewSummary(
      toPartnerShopReviewSummary(draft, partnerShopReviewSummary)
    );

    onChangeVerifiedReasonsJson(
      normalizeVerifiedReasonsJson(
        draft.verifiedReasonsJson,
        verifiedReasonsJson
      )
    );

    message.success('선택한 초안을 입력 폼에 불러왔습니다.');
  };

  const handleConfirmCurrentSummary = () => {
    onChangePartnerShopReviewSummary({
      ...partnerShopReviewSummary,
      status: 'READY',
      delYn: 0,
    });

    message.success('현재 후기 요약 내용을 사용하도록 반영했습니다.');
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              후기 요약
            </Title>
            <Text type="secondary">
              기존 초안을 불러오거나 새로 생성한 뒤, 고객에게 보여줄 최종 문구를 다듬습니다.
            </Text>
          </div>

          {!hasSourceCafeExtractedShops && (
            <Alert
              type="warning"
              showIcon
              message="연결된 업체가 없습니다."
              description="먼저 첫 단계에서 업체를 선택해야 후기 요약 초안을 불러올 수 있습니다."
            />
          )}

          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              loading={loadingDrafts}
              disabled={!hasSourceCafeExtractedShops}
              onClick={loadReviewSummaryDrafts}
            >
              초안 목록 새로고침
            </Button>

            <Button
              type="primary"
              icon={<RobotOutlined />}
              loading={generatingDraft}
              disabled={!hasSourceCafeExtractedShops}
              onClick={handleGenerateReviewSummaryDraft}
            >
              새 AI 초안 생성
            </Button>

            <Button
              icon={<CheckOutlined />}
              disabled={!partnerShopReviewSummary.summaryText.trim()}
              onClick={handleConfirmCurrentSummary}
            >
              현재 내용 사용
            </Button>
          </Space>
        </Space>
      </Card>

      <Row gutter={16} align="top">
        <Col xs={24} lg={8}>
          <Card
            title="생성된 초안"
            extra={<Tag>{reviewSummaryDrafts.length.toLocaleString()}개</Tag>}
            style={{ position: 'sticky', top: 80 }}
          >
            {loadingDrafts ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <Spin />
              </div>
            ) : reviewSummaryDrafts.length === 0 ? (
              <Empty description="아직 생성된 초안이 없습니다.">
                <Button
                  type="primary"
                  icon={<RobotOutlined />}
                  disabled={!hasSourceCafeExtractedShops}
                  loading={generatingDraft}
                  onClick={handleGenerateReviewSummaryDraft}
                >
                  첫 초안 생성
                </Button>
              </Empty>
            ) : (
              <List
                dataSource={reviewSummaryDrafts}
                renderItem={(draft) => {
                  const isSelected = Number(draft.id) === Number(selectedDraftId);

                  return (
                    <List.Item style={{ padding: '8px 0' }}>
                      <Card
                        size="small"
                        style={{
                          width: '100%',
                          borderColor: isSelected ? '#1677ff' : undefined,
                        }}
                        bodyStyle={{ padding: 12 }}
                      >
                        <Space
                          direction="vertical"
                          size={8}
                          style={{ width: '100%' }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 8,
                            }}
                          >
                            <Text strong ellipsis style={{ flex: 1 }}>
                              {getDraftTitle(draft)}
                            </Text>

                            {isSelected && <Tag color="blue">사용 중</Tag>}
                          </div>

                          <Paragraph
                            type="secondary"
                            ellipsis={{ rows: 2 }}
                            style={{ marginBottom: 0 }}
                          >
                            {draft.summaryText || '요약 내용 없음'}
                          </Paragraph>

                          {draft.plannerText && (
                            <Paragraph
                              ellipsis={{ rows: 2 }}
                              style={{ marginBottom: 0, fontSize: 12 }}
                            >
                              <Text strong>플래너의 한마디 </Text>
                              {draft.plannerText}
                            </Paragraph>
                          )}

                          <Space wrap size={4}>
                            <Tag>{draft.modelName || 'AI'}</Tag>
                            <Tag>
                              근거 {getDraftSourceCount(draft).toLocaleString()}개
                            </Tag>
                            {getDraftDateText(draft) && (
                              <Tag>{getDraftDateText(draft)}</Tag>
                            )}
                          </Space>

                          {Array.isArray(draft.verifiedReasonsJson) &&
                            draft.verifiedReasonsJson.length > 0 && (
                              <Space
                                direction="vertical"
                                size={4}
                                style={{ width: '100%' }}
                              >
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  추천 Point
                                </Text>

                                <Space wrap size={4}>
                                  {draft.verifiedReasonsJson
                                    .slice(0, 3)
                                    .map((item, index) => (
                                      <Tag key={`${item.title}-${index}`}>
                                        {item.title}
                                      </Tag>
                                    ))}
                                </Space>
                              </Space>
                            )}

                          <Button
                            block
                            type={isSelected ? 'primary' : 'default'}
                            onClick={() => handleUseReviewSummaryDraft(draft)}
                          >
                            이 초안 사용
                          </Button>
                        </Space>
                      </Card>
                    </List.Item>
                  );
                }}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={16}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row gutter={12}>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="전체 언급"
                    value={partnerShopReviewSummary.totalMentionCount || 0}
                    suffix="개"
                  />
                </Card>
              </Col>

              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="평가 후기"
                    value={partnerShopReviewSummary.evaluatedReviewCount || 0}
                    suffix="개"
                  />
                </Card>
              </Col>

              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="직접 후기"
                    value={partnerShopReviewSummary.directReviewCount || 0}
                    suffix="개"
                  />
                </Card>
              </Col>

              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="간접 후기"
                    value={partnerShopReviewSummary.indirectReviewCount || 0}
                    suffix="개"
                  />
                </Card>
              </Col>
            </Row>

            {/* <Card size="small" title="추천 Point 소개 문구">
              <TextArea
                value={verifiedReasonsJson.introText || ''}
                rows={5}
                placeholder={`예: 신랑님, 신부님! 강남 최대 규모와 합리적인 가격을 모두 잡은 헤리츠테일러를 추천드리는 핵심 이유를 정리해 드릴게요. 헤리츠테일러는 넓고 쾌적한 매장에서 프라이빗한 상담이 가능하며, 실제 고객님들의 만족도가 높은 포인트만 콕 집어서 안내해 드릴게요.`}
                onChange={(event) =>
                  onChangeVerifiedReasonsJson({
                    ...verifiedReasonsJson,
                    introText: event.target.value,
                  })
                }
              />
            </Card> */}
            <TextItemEditor
              title="추천 Point"
              items={verifiedReasonsJson.items || []}
              onChange={(nextItems) =>
                onChangeVerifiedReasonsJson({
                  ...verifiedReasonsJson,
                  items: nextItems,
                })
              }
              addButtonText="추천 Point 추가"
              titlePlaceholder="예: 높은 맞춤 예복 완성도"
              descriptionPlaceholder="예: 실제 후기에서 핏, 디자인, 원단 만족도가 반복적으로 확인됩니다."
            />
            <Card size="small" title="리뷰 요약">
              <TextArea
                value={partnerShopReviewSummary.summaryText}
                rows={6}
                placeholder="업체 후기 전체를 2~3문장으로 요약해 주세요."
                onChange={(event) =>
                  onChangePartnerShopReviewSummary({
                    ...partnerShopReviewSummary,
                    summaryText: event.target.value,
                  })
                }
              />
            </Card>
            <Card size="small" title="플래너의 한마디">
              <TextArea
                value={partnerShopReviewSummary.plannerText || ''}
                rows={4}
                placeholder="플래너가 고객에게 직접 추천하는 느낌의 한마디를 입력해 주세요."
                onChange={(event) =>
                  onChangePartnerShopReviewSummary({
                    ...partnerShopReviewSummary,
                    plannerText: event.target.value,
                  })
                }
              />
            </Card>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <TextItemEditor
                  title="좋았던 점"
                  items={partnerShopReviewSummary.prosJson || []}
                  onChange={(nextItems) =>
                    onChangePartnerShopReviewSummary({
                      ...partnerShopReviewSummary,
                      prosJson: nextItems,
                    })
                  }
                  addButtonText="좋았던 점 추가"
                  titlePlaceholder="예: 핏과 체형 보정 만족도"
                  descriptionPlaceholder="예: 여러 후기에서 어깨선, 소매, 허리 라인을 체형에 맞게 조정해준다는 평가가 확인됩니다."
                />
              </Col>

              <Col xs={24} md={12}>
                <TextItemEditor
                  title="확인할 점"
                  items={partnerShopReviewSummary.consJson || []}
                  onChange={(nextItems) =>
                    onChangePartnerShopReviewSummary({
                      ...partnerShopReviewSummary,
                      consJson: nextItems,
                    })
                  }
                  addButtonText="확인할 점 추가"
                  titlePlaceholder="예: 비용 구성 확인 필요"
                  descriptionPlaceholder="예: 원단과 구성에 따라 금액 차이가 있을 수 있어 상담 전 예산 범위를 확인하는 것이 좋습니다."
                />
              </Col>
            </Row>

            <Card size="small" title="대표 키워드">
              <Select
                mode="tags"
                value={partnerShopReviewSummary.keywordsJson || []}
                style={{ width: '100%' }}
                placeholder="예: 맞춤예복, 가봉, 촬영예복, 원단, 수선"
                onChange={(nextKeywords) =>
                  onChangePartnerShopReviewSummary({
                    ...partnerShopReviewSummary,
                    keywordsJson: nextKeywords,
                  })
                }
              />
            </Card>
          </Space>
        </Col>
      </Row>
    </Space>
  );
}
